import React from 'react';
import { Button, Input } from '../../StyledComponents';

export interface FlightHubDroneModelInfo {
	key: string;
	domain: string;
	type: string;
	subType: string;
	name: string;
	deviceClass: string;
}

export interface AvailableDroneSn {
	sn: string;
	callsign: string;
	online: boolean;
	modeCode?: number;
	deviceModel?: FlightHubDroneModelInfo;
}

export interface DronePerfForm {
	reachDiameter: string;
	flightSpeed: string;
	startStopTime: string;
	maxFlightTime: string;
}

interface DroneCreateEditPanelProps {
	mode: 'create' | 'edit';
	name: string;
	perf: DronePerfForm;
	availableDroneSns: AvailableDroneSn[];
	selectedSn: string;
	selectedSnInfo?: AvailableDroneSn;
	selectedSnLabel: string;
	isSnDropdownOpen: boolean;
	canSave: boolean;
	saving: boolean;
	onNameChange: (value: string) => void;
	onPerfChange: React.Dispatch<React.SetStateAction<DronePerfForm>>;
	onSelectedSnChange: (value: string) => void;
	onSnDropdownOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
	onCancel: () => void;
	onSave: () => void;
}

export const DroneCreateEditPanel: React.FC<DroneCreateEditPanelProps> = ({
	mode,
	name,
	perf,
	availableDroneSns,
	selectedSn,
	selectedSnInfo,
	selectedSnLabel,
	isSnDropdownOpen,
	canSave,
	saving,
	onNameChange,
	onPerfChange,
	onSelectedSnChange,
	onSnDropdownOpenChange,
	onCancel,
	onSave,
}) => {
	const isCreate = mode === 'create';

	return (
		<div className="w-full max-w-[620px]">
			<div className="rounded-[20px] border-2 border-[#0063A3] bg-white p-8">
				<h2 className="mb-6 text-2xl font-semibold text-[#333333]">
					{isCreate ? 'Neue Drohne hinzufügen' : 'Drohne bearbeiten'}
				</h2>

				{isCreate && (
					<div className="mb-8 text-base text-[#666666]">
						<p>FlightHub Drohnen-SN *</p>
						{availableDroneSns.length > 0 ? (
							<div className="fleet-select-wrapper mt-2">
								<button
									type="button"
									className="styled-input select fleet-select text-left"
									onClick={() => onSnDropdownOpenChange((isOpen) => !isOpen)}
								>
									{selectedSnLabel}
								</button>

								{isSnDropdownOpen && (
									<div className="fleet-select-options">
										{availableDroneSns.map((droneSn) => (
											<button
												key={droneSn.sn}
												type="button"
												className={
													'fleet-select-option' +
													(selectedSn === droneSn.sn ? ' selected' : '')
												}
												onClick={() => {
													onSelectedSnChange(droneSn.sn);
													onSnDropdownOpenChange(false);
												}}
											>
												<span>
													{droneSn.callsign} ({droneSn.sn})
												</span>
												<span>{droneSn.online ? 'Online' : 'Offline'}</span>
											</button>
										))}
									</div>
								)}
							</div>
						) : (
							<p className="italic">
								Keine verfügbaren FlightHub-Drohnen gefunden.
							</p>
						)}

						{selectedSnInfo && (
							<div className="mt-3 rounded-lg bg-[#EDF4FA] p-4 text-sm text-[#333333]">
								<p className="font-semibold">{selectedSnInfo.callsign}</p>
								<p>Serial: {selectedSnInfo.sn}</p>
								{selectedSnInfo.deviceModel && (
									<p>Modell: {selectedSnInfo.deviceModel.name}</p>
								)}
							</div>
						)}
					</div>
				)}

				<div className="mb-8 h-px bg-[#333333]" />

				<div className="flex flex-col gap-6">
					<Input
						label="Name*"
						value={name}
						placeholder="Geben Sie der Drohnen einen Namen"
						onChange={onNameChange}
					/>

					<div className="grid grid-cols-2 gap-6">
						<Input
							label="Reichweite (m)"
							type="number"
							value={perf.reachDiameter}
							onChange={(value) =>
								onPerfChange((currentPerf) => ({
									...currentPerf,
									reachDiameter: value,
								}))
							}
						/>
						<Input
							label="Geschwindigkeit (m/s)"
							type="number"
							value={perf.flightSpeed}
							onChange={(value) =>
								onPerfChange((currentPerf) => ({
									...currentPerf,
									flightSpeed: value,
								}))
							}
						/>
						<Input
							label="Max. Flugzeit (min)"
							type="number"
							value={perf.maxFlightTime}
							onChange={(value) =>
								onPerfChange((currentPerf) => ({
									...currentPerf,
									maxFlightTime: value,
								}))
							}
						/>
						<Input
							label="Start/Stop Zeit (s)"
							type="number"
							value={perf.startStopTime}
							step={0.1}
							onChange={(value) =>
								onPerfChange((currentPerf) => ({
									...currentPerf,
									startStopTime: value,
								}))
							}
						/>
					</div>
				</div>
			</div>

			<div className="mt-8 flex justify-end gap-5">
				<Button
					className="h-12 rounded-full border border-[#E1320F] px-8 text-sm font-semibold text-[#E1320F]"
					onClick={onCancel}
				>
					Abbrechen
				</Button>
				<Button
					className="h-12 rounded-full bg-[#E1320F] px-10 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
					enabled={canSave && !saving}
					onClick={onSave}
					primary
				>
					{saving ? 'Speichern...' : isCreate ? 'Hinzufügen' : 'Speichern'}
				</Button>
			</div>
		</div>
	);
};
